import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MaterialTable from "material-table";
import moment from "moment";
import "moment/min/locales";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Card, DropdownItem, DropdownMenu, DropdownToggle, Modal, ModalBody, UncontrolledDropdown } from "reactstrap";
import { siteAlarmNotificationsEnabledSet, siteAlarmRecordingEnabledSet, siteBillingSet, siteDateCreatedSet, siteMaxAgeSet, siteProductTypeSet, siteSmsAlarmNotificationsEnabledSet, siteStats } from "../../api/site";
import { environments, features, getEnvironment, isFeatureEnabled } from "../../config";
import { tableIcons } from "../../helpers/tableIcons";
import local from "../../localization/strings";
import AddButton from "../common/AddButton";
import CancelButton from "../common/CancelButton";
import FontAwesomeButton from "../common/FontAwesomeButton";
import Loader from "../common/Loader";
import DisplaySiteId from "./DisplaySiteId";
import SiteAccess from "./SiteAccess";
import SiteConfigure from "./SiteConfigure";
import SiteUserEdit from "./SiteUserEdit";
import SiteUsers from "./SiteUsers";
import SmsUsage from "./SmsUsage";
import { faBell, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { alarmTeamEmergency } from "../../api/alarmTeams";
import { SiteRequiresMFA } from "../../api/auth";
import ConfigureMfa from "../mfa/ConfigureMfa";
import MFA from "../mfa/MFA";
import SiteBilling from "./SiteBilling";
import SiteSms from "./SiteSms";

const SiteList = ({ add, addDemo }) => {
	const [accessSiteId, setAccessSiteId] = useState(null);
	const [displaySiteId, setDisplaySiteId] = useState(null);
	const [displayName, setDisplayName] = useState(null);
	const [data, setData] = useState(undefined);
	const [showLoading, setShowLoading] = useState(true);
	const [showFirstReadingDate, setShowFirstReadingDate] = useState(false);
	const [focusSite, setFocusSite] = useState(undefined);
	const [focusUserId, setFocusUserId] = useState(undefined);
	const [mode, setMode] = useState("List");
	const [demo, setDemo] = useState(false);
	const [mfaSiteId, setMFASiteId] = useState(undefined);
	const [searchSite, setSearchSite] = useState("");
	const [searchType, setSearchType] = useState("");
	const [searchDistributor, setSearchDistributor] = useState("");
	const [searchInvoice, setSearchInvoice] = useState("");

	const LoadData = useCallback(async () => {
		setData(await siteStats(showFirstReadingDate));
	}, [showFirstReadingDate]);

	useEffect(() => {
		LoadData();
		if (getEnvironment() === environments.Demo) {
			setDemo(true);
		}
		setShowLoading(false);
	}, [LoadData]);

	const toggleShowFirstReadingDate = async (newValue) => {
		setShowFirstReadingDate(newValue);
		setData(await siteStats(newValue));
	};

	const trigger = (site, newMode) => {
		setFocusSite(site);
		setMode(newMode);
	};

	const cancel = async () => {
		setMode("List");
		setFocusUserId(undefined);
		setFocusSite(undefined);
		setData(await siteStats(showFirstReadingDate));
	};

	const saveConf = async (e, siteId, siteProduct, userLimit, channelLimit, siteAlarmsEnabled, siteAlarmRecordingEnabled, siteSmsAlarmNotificationsEnabled, siteMaxAge, dateCreated) => {
		e.preventDefault();

		await siteProductTypeSet(siteId, siteProduct, userLimit, channelLimit);
		await siteMaxAgeSet(siteId, siteMaxAge);
		await siteAlarmNotificationsEnabledSet(siteId, siteAlarmsEnabled);
		await siteAlarmRecordingEnabledSet(siteId, siteAlarmRecordingEnabled);
		await siteSmsAlarmNotificationsEnabledSet(siteId, siteSmsAlarmNotificationsEnabled);
		await siteDateCreatedSet(siteId, dateCreated);

		setData(await siteStats(showFirstReadingDate));
	};

	const saveBilling = async (e, siteId, amazonEC2, simCard, simCardCount, salesOrderNumber, salesOrderNumberLatest, contractStartDate) => {
		e.preventDefault();

		await siteBillingSet(siteId, amazonEC2, simCard, simCardCount, salesOrderNumber, salesOrderNumberLatest, contractStartDate);

		setData(await siteStats(showFirstReadingDate));

		cancel();
		toast.success(local.TF_Site_Billing_changed);
	};

	const editUser = (id) => {
		setFocusUserId(id);
		setMode("UserEdit");
	};

	const saveUser = () => {
		setMode("Users");
		setFocusUserId(undefined);
		LoadData();
	};

	const siteDetails = (name, id) => {
		setDisplayName(name);
		setDisplaySiteId(id);
	};

	const activateEmergencyAlarms = async (id) => {
		await alarmTeamEmergency(id, true);
		var allSites = await siteStats(false);
		setData(allSites);
	};
	const deactivateEmergencyAlarms = async (id) => {
		await alarmTeamEmergency(id, false);
		var allSites = await siteStats(false);
		setData(allSites);
	};

	const columns = [
		{
			title: local.TS_Actions,
			render: (rowData) => (
				<>
					{SiteRequiresMFA(rowData.id) ? (
						<>
							<FontAwesomeIcon className="danger-icon fs-1 mr-2 invisible" icon={faExclamationCircle} />
							<FontAwesomeIcon className="danger-icon fs-1 mr-2 invisible" icon={faExclamationCircle} />
							<FontAwesomeIcon icon="lock" className="cursor-pointer" onClick={() => setMFASiteId(rowData.id)} />
						</>
					) : (
						<div className="d-flex flex-nowrap justify-content-center">
							{rowData.darcaHubOffline ? <FontAwesomeIcon className="danger-icon fs-1 mr-2" icon={faExclamationCircle} title={local.TF_Darca_hub_offline} /> : <FontAwesomeIcon className="danger-icon fs-1 mr-2 invisible" icon={faExclamationCircle} />}
							{rowData.emergencyAlarmCountOn > 0 ? <FontAwesomeIcon className="danger-icon fs-1 mr-2" icon={faBell} title={local.TS_Emergency_Alarms_Enabled} /> : <FontAwesomeIcon className="danger-icon fs-1 mr-2 invisible" icon={faExclamationCircle} />}
							<UncontrolledDropdown>
								<DropdownToggle tag="div" className="pin-button">
									<FontAwesomeIcon icon="ellipsis-v" fixedWidth />
								</DropdownToggle>
								<DropdownMenu className="border" positionFixed>
									<DropdownItem onClick={() => setAccessSiteId(rowData.id)}>{local.TF_Access_Site}</DropdownItem>
									<DropdownItem divider />
									<DropdownItem onClick={() => trigger(rowData, "Configure")}>{local.TF_Configure_Site}</DropdownItem>
									<DropdownItem onClick={() => trigger(rowData, "ConfigureMFA")}>{local.TF_Configure_MFA}</DropdownItem>
									<DropdownItem onClick={() => siteDetails(rowData.displayName, rowData.id)}>{local.TF_Display_Site_ID}</DropdownItem>
									<DropdownItem onClick={() => trigger(rowData, "Billing")}>{local.TF_Billing_Information}</DropdownItem>
									<DropdownItem onClick={() => trigger(rowData, "Sms")}>{local.TF_Sms_Information}</DropdownItem>
									<DropdownItem divider />
									<DropdownItem onClick={() => trigger(rowData, "Users")}>{local.TS_Users}</DropdownItem>
									{(rowData.emergencyAlarmCountOff > 0 || rowData.emergencyAlarmCountOn > 0) && <DropdownItem divider />}
									{rowData.emergencyAlarmCountOff > 0 && (
										<DropdownItem onClick={() => activateEmergencyAlarms(rowData.id)}>
											{local.TS_Emergency_Alarms_Enable}
											<div className="fs--2">({local.TS_Emergency_Alarms_Warn})</div>
										</DropdownItem>
									)}
									{rowData.emergencyAlarmCountOn > 0 && <DropdownItem onClick={() => deactivateEmergencyAlarms(rowData.id)}>{local.TS_Emergency_Alarms_Disable}</DropdownItem>}
								</DropdownMenu>
							</UncontrolledDropdown>
						</div>
					)}
				</>
			),
		},
		{ title: local.TF_Site_Name, field: "displayName", defaultSort: "asc" },
		{ title: local.TF_Description, field: "description" },
		{ title: local.TS_Type, field: "siteType" },
		{ title: local.TF_Distributor, field: "distributorName" },
		{
			title: local.TF_ContractStart,
			field: "contractStartDate",
			render: (rowData) => <div>{rowData.contractStartDate ? moment(rowData.contractStartDate).format("DD MMM YYYY") : ""}</div>,
		},
		{
			title: local.TF_Sales_Order_Number,
			field: "salesOrderNumberDisplay",
		},
		{
			title: local.TF_Sales_Order_Number_Latest,
			field: "salesOrderNumberLatestDisplay",
		},
		{
			title: local.TF_ProductType,
			field: "productType",
			render: (rowData) => (
				<>
					{rowData.productType}
					{rowData.maxReadingsAgeMonths && <div>({rowData.maxReadingsAgeMonths} months)</div>}
				</>
			),
		},
		{ title: local.TF_NumberOfUsers, filtering: false, field: "userCount" },
		{ title: local.TF_NumberOfTransmitters, filtering: false, field: "transmitterCount" },
		{ title: local.TF_NumberOfTransmitterChannels, filtering: false, field: "channelsWithTransmitterCount" },
		{
			title: local.TF_Data_from,
			filtering: false,
			field: "latestReadingDateTime",
			export: false,
			render: (rowData) => <div>{rowData.latestReadingDateTime ? moment(rowData.latestReadingDateTime).format("DD MMM YYYY HH:mm") : ""}</div>,
		},
		{
			title: local.TF_Data_from,
			filtering: false,
			field: "latestReadingDateTimeFormatted",
			export: true,
			cellStyle: { display: "none" },
			headerStyle: { display: "none" },
		},
		{
			title: local.TF_Alarms,
			filtering: false,
			field: "alarmNotificationsEnabled",
			render: (rowData) => (
				<>
					{rowData.alarmRecordingEnabled ? <FontAwesomeIcon size="lg" icon="microphone" className="text-success" fixedWidth /> : <FontAwesomeIcon size="lg" icon="microphone-slash" fixedWidth className="text-secondary" />}
					{rowData.alarmNotificationsEnabled ? <FontAwesomeIcon size="lg" icon="lightbulb" className="text-success" fixedWidth /> : <FontAwesomeIcon size="lg" icon="lightbulb-slash" fixedWidth className="text-secondary" />}
					{rowData.smsAlarmNotificationsEnabled ? <FontAwesomeIcon size="lg" icon="comment" className="text-success" fixedWidth /> : <FontAwesomeIcon size="lg" icon="comment-slash" fixedWidth className="text-secondary" />}
				</>
			),
		},
		{ title: local.TF_SMS_Usage, filtering: false, field: "smsUsage", render: (rowData) => <SmsUsage limit={rowData.annualSmsSegmentLimit} remaining={rowData.activeSmsSegmentsRemaining} siteId={rowData.id} /> },
		{
			title: local.TF_Created,
			field: "dateCreated",
			export: false,
			render: (rowData) => <div>{moment(rowData.dateCreated).format("DD MMM YYYY")}</div>,
		},
		{
			title: local.TF_Created,
			filtering: false,
			field: "dateCreatedFormatted",
			export: true,
			cellStyle: { display: "none" },
			headerStyle: { display: "none" },
		},
		{
			title: local.TF_FirstReading,
			filtering: false,
			field: "firstReadingDateTime",
			export: false,
			render: (rowData) => <div>{rowData.firstReadingDateTime ? moment(rowData.firstReadingDateTime).format("DD MMM YYYY") : ""}</div>,
			cellStyle: { display: showFirstReadingDate ? "" : "none" },
			headerStyle: { display: showFirstReadingDate ? "" : "none" },
		},
		{
			title: local.TF_FirstReading,
			filtering: false,
			field: "firstReadingDateTimeFormatted",
			export: true,
			cellStyle: { display: "none" },
			headerStyle: { display: "none" },
		},
		{
			title: local.TF_Site_ID,
			filtering: false,
			field: "id",
			export: true,
			cellStyle: { display: "none" },
			headerStyle: { display: "none" },
		},
		{ title: "", field: "attributes", filtering: false },
	];

	const memoData = useMemo(() => {
		return data
			?.filter((x) => !searchSite || (x.displayName ?? "").toLowerCase().includes(searchSite.toLowerCase()) || (x.description ?? "").toLowerCase().includes(searchSite.toLowerCase()))
			?.filter((x) => !searchType || (x.siteType ?? "").toLowerCase() === searchType.toLowerCase())
			?.filter((x) => !searchDistributor || (x.distributorName ?? "").toLowerCase().includes(searchDistributor.toLowerCase()))
			?.filter((x) => !searchInvoice || (x.salesOrderNumberDisplay ?? "").toLowerCase().includes(searchInvoice.toLowerCase()) || (x.salesOrderNumberLatestDisplay ?? "").toLowerCase().includes(searchInvoice.toLowerCase()));
	}, [data, searchSite, searchType, searchDistributor, searchInvoice]);

	return (
		<>
			{/* MODALS */}

			{/* -- Access MFA */}
			<Modal isOpen={!!mfaSiteId} toggle={() => setMFASiteId(undefined)} centered backdrop="static">
				<ModalBody>
					<MFA siteId={mfaSiteId} onCancel={() => setMFASiteId(undefined)} />
				</ModalBody>
			</Modal>

			{/* -- Access Site */}
			<SiteAccess siteId={accessSiteId} cancelAccessSite={() => setAccessSiteId(undefined)} />

			{/* -- Display Site Id */}
			<DisplaySiteId siteId={displaySiteId} siteName={displayName} cancelDisplaySiteId={() => setDisplaySiteId(false)} />

			{/* !-- All of the enclosed need a focus site to work --! */}
			{focusSite && (
				<>
					{/* -- Users */}
					<Modal isOpen={mode === "Users"} centered backdrop="static" size="xl">
						<ModalBody>
							<span className="d-flex justify-content-end align-items-center">
								<CancelButton string={local.TS_Back} buttonClasses={"mt-n2 mb-2 mr-2"} clickFunction={cancel} />
								<AddButton string={local.TF_Add_User} buttonClasses={"mt-n2 mb-2"} clickFunction={() => editUser(undefined)} />
							</span>

							<SiteUsers site={focusSite} editUser={editUser} />
						</ModalBody>
					</Modal>

					{/* -- User Edit */}
					<Modal isOpen={mode === "UserEdit"} centered backdrop="static" size="xl">
						<ModalBody>
							<SiteUserEdit site={focusSite} userId={focusUserId} done={saveUser} />
						</ModalBody>
					</Modal>

					{/* -- Configure */}
					<Modal isOpen={mode === "Configure"} centered backdrop="static" size="xl">
						<ModalBody>
							<SiteConfigure site={focusSite} cancel={cancel} save={saveConf} />
						</ModalBody>
					</Modal>

					{/* -- Billing */}
					<Modal isOpen={mode === "Billing"} centered backdrop="static" size="xl">
						<ModalBody>
							<SiteBilling site={focusSite} cancel={cancel} save={saveBilling} />
						</ModalBody>
					</Modal>

					{/* -- Sms */}
					<Modal isOpen={mode === "Sms"} centered backdrop="static" size="xl">
						<ModalBody>
							<span className="d-flex justify-content-end align-items-center">
								<CancelButton string={local.TS_Back} buttonClasses={"mt-n2 mb-2 mr-2"} clickFunction={cancel} />
							</span>

							<SiteSms site={focusSite} />
						</ModalBody>
					</Modal>

					{/* -- Configure MFA */}
					<Modal isOpen={mode === "ConfigureMFA"} centered backdrop="static" size="xl">
						<ModalBody>
							<ConfigureMfa site={focusSite} cancel={cancel} showCard={false} />
						</ModalBody>
					</Modal>
				</>
			)}

			{/* MAIN AREA */}
			<span className="float-right pt-2 mt-n5">
				{mode === "List" && <FontAwesomeButton color="secondary" icon="puzzle-piece" text={local.TF_Toggle_First_Reading} onClick={() => toggleShowFirstReadingDate(!showFirstReadingDate)} />}
				{isFeatureEnabled(features.SiteCreate) && <>{demo ? <AddButton string="Add Demo Site" clickFunction={addDemo} /> : <AddButton string={local.TF_Create_Site} clickFunction={add} />}</>}
			</span>

			<Card className="placeholder-card">
				{showLoading && <Loader />}
				{!showLoading && data && (
					<>
						<div className="container p-2">
							<div className="row">
								<div className="col-6 pt-2 col-lg-3">
									<h6>{local.TF_Site}:</h6>
									<input type="text" className="form-control" value={searchSite} onChange={(e) => setSearchSite(e.currentTarget.value)} />
								</div>
								<div className="col-6 pt-2 col-lg-3">
									<h6>{local.TS_Type}:</h6>
									<select className="form-control" value={searchType} onChange={(e) => setSearchType(e.currentTarget.value)}>
										<option value="" text="" />
										<option value="Customer">{local.TF_Customer}</option>
										<option value="Eltek">{local.TF_Eltek}</option>
										<option value="Archived">{local.TF_Archived}</option>
									</select>
								</div>
								<div className="col-6 pt-2 col-lg-3">
									<h6>{local.TF_Distributor}:</h6>
									<input type="text" className="form-control" value={searchDistributor} onChange={(e) => setSearchDistributor(e.currentTarget.value)} />
								</div>
								<div className="col-6 pt-2 col-lg-3">
									<h6>{local.TF_Sales_Order_Number}:</h6>
									<input type="text" className="form-control" value={searchInvoice} onChange={(e) => setSearchInvoice(e.currentTarget.value)} />
								</div>
							</div>
						</div>
						<div className="MuiTable MuiTable-NoShadow text-center">
							<MaterialTable
								columns={columns}
								data={memoData}
								title=""
								icons={tableIcons}
								options={{
									search: false,
									padding: "dense",
									sorting: true,
									exportButton: true,
									exportAllData: true,
									exportFileName: "Site List",
									// filtering: true,
									paging: true,
									pageSize: 20,
									pageSizeOptions: [20, 50, 100],
									emptyRowsWhenPaging: false,
									headerStyle: {
										backgroundColor: "#fff",
										color: "#000",
									},
									rowStyle: (rowData) => ({
										textAlign: "center",
										backgroundColor: !rowData.alarmRecordingEnabled || !rowData.alarmNotificationsEnabled ? "#fad7dd" : "white",
									}),
								}}
							/>
						</div>
					</>
				)}
			</Card>
		</>
	);
};

export default SiteList;
